<template>
  <div
    v-if="loading || loadingNewFeature"
    class="npbtn npbtn-filled"
    style="display: block; padding: 0;"
  >
    <LoadIcon class="is-loading" />
  </div>
  <div
    v-else
    class="d-flex"
  >
    <div class="d-flex flex-column">
      <v-menu
        v-model="menuSelected"
        :close-on-content-click="false"
      >
        <template #activator="{props, isActive}">
          <np-action-btn
            data-testid="add-feature-button"
            :append-icon="isActive ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            v-bind="props"
            @click="handleAddFeatureClick"
          >
            Add Feature
          </np-action-btn>
        </template>
        <template #default>
          <v-list>
            <AddFeatureItem
              v-for="[featureKey, featureOption] in addableFeatures"
              :key="featureKey"
              :feature-key="featureKey"
              :feature-option="featureOption"
              @handle-click="handleOptionClick"
            />
          </v-list>
        </template>
      </v-menu>
      <!-- File selector for image uploads -->
      <input
        ref="fileUploadInput"
        type="file"
        accept="application/vnd.google-earth, .kmz, .kml, image/png, image/jpeg"
        class="fileInput"
      >
    </div>
  </div>
</template>

<script>
// 3rd Party Libraries
import {saveImage} from '@/services/CloudStorageService';
import {mapState, mapMutations, mapActions} from 'vuex';
import {parse, format} from 'path';

// Services and Helpers
import {importKMx} from '@/lib/kml/kmlImport';
import {importImage} from '@/lib/image/imageImport';
import {useMenuClickListener} from '@/mixins.js';
import {MapService} from '@/services/MapService';
import {MAX_POLYGON_VERTICES} from '@/services/GraphicService/PolyGraphic/PolygonGraphic';

// Components
import LoadIcon from '@/components/svgs/LoadIcon.vue';
import AddFeatureItem from '@/components/FeatureMenuItems/AddFeatureItem.vue';

/**
 * @typedef {import('@/services/OverlaysService/Overlay').Overlay} Overlay
 * @typedef {import('@/lib/classes/SharedMap').SharedMap} SharedMap
 */

export default {
  name: 'AddFeatureButton',
  components: {
    LoadIcon,
    AddFeatureItem,
  },
  mixins: [useMenuClickListener('handleMenuClick')],
  props: {
    allowDrivetimePolygons: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['addNewFeature'],
  data() {
    return {
      loading: false,
      menuSelected: false,
    };
  },
  computed: {
    ...mapState(['addableFeatures', 'loadingNewFeature', 'canImportImagesAtAnyZoom']),
  },
  mounted() {
    // initialize the addable features at the current zoom level
    this.refreshAddableFeatures(MapService.getMap().getZoom());
    // whenever the zoom level changes, refresh the addable features so that ones outside of
    // their accepted zoom levels are grayed out
    MapService.addMapListener('addFeatureButtonZoomChanged', 'zoom_changed', () => {
      this.refreshAddableFeatures(MapService.getMap().getZoom());
    });
  },
  unmounted() {
    MapService.removeMapListener('addFeatureButtonZoomChanged', 'zoom_changed');
  },
  methods: {
    ...mapMutations(['refreshAddableFeatures']),
    ...mapActions(['addErrorMessage', 'setEditingFeature']),
    ...mapActions('infowindow', ['closeInfoWindow']),
    async handleAddFeatureClick() {
      this.setEditingFeature(null);
      this.closeInfoWindow();
      // Ensure "Project Overlays" section is visible/checked
      await this.$store.dispatch('filters/setSectionChecked', {
        market: 'Industrial',
        section: 'Project Overlays',
        checked: true,
      });
    },
    /**
     * Closes the dropdown and invokes the appropriate callback when an option is clicked.
     * @param {string} featureKey The clicked option key
     * @param {object} featureOption The clicked option object
     */
    handleOptionClick(featureKey, featureOption) {
      this.closeInfoWindow();
      if (featureKey === 'png') {
        this.$emit('addNewFeature', this.handleFileClick);
      } else {
        this.menuSelected = false;
        this.$emit('addNewFeature', featureOption.createNewFeature);
      }
    },
    /**
     * Adds handleFiles as an event listener to the file input before triggering the click event on the hidden input.
     * @param {Overlay|SharedMap} parent The overlay or shared map to add the feature to.
     * @param {Function} finishedCallback The callback to invoke when the feature has been added.
     */
    handleFileClick(parent, finishedCallback) {
      const loadFile = async (file) => {
        if (file) {
          this.menuSelected = false; // close the dropdown if a file is selected
        }
        this.loading = true;
        try {
          const extension = file.name.toLowerCase().split('.').pop();
          let features = [];
          let folders = [];
          let shouldPanToFirst;
          if (extension === 'kmz' || extension === 'kml') {
            const result = await importKMx(file);
            features = result.features;
            folders = result.folders;
            shouldPanToFirst = true;
          } else if (extension === 'png' || extension === 'jpg' || extension === 'jpeg') {
            // If a user cannot import images at any zoom level, we restrict the zoom level
            const minZoom = 14;
            if (!this.canImportImagesAtAnyZoom && MapService.getMap().getZoom() < minZoom) {
              MapService.getMap().setZoom(minZoom);
            }
            features = [await importImage(file)];
            shouldPanToFirst = false;
          } else {
            throw new Error(`Unsupported file type '${extension}'`);
          }
          const newFeaturePromises = [];
          for (const [index, feature] of features.entries()) {
            if (feature.graphicData.featureType === 'png') {
              const path = parse(feature.graphicData.imagePath);
              path.base = `${feature.name}-${Date.now()}-${feature.geohash}${path.ext}`;
              const result = await saveImage(feature.graphicData.image, format(path));
              feature.graphicData.imagePath = result.metadata.fullPath;
              // now that the image is saved to GCS, we don't need to store it in the feature or send it to Firestore
              delete feature.graphicData.image;
            }
            // If we do not allow drivetime polygons and the user uploaded a polygon, we have to check its vertex count
            if (!this.allowDrivetimePolygons && feature.graphicData.featureType === 'polygon') {
              // If the polygon has too many vertices, inform the user it cannot be uploaded
              if (feature.graphicData.polys[0].length > MAX_POLYGON_VERTICES) {
                this.addErrorMessage('The KMZ file contains a polygon with too many vertices. Please simplify the polygon and try again.');
                continue;
              }
            }
            if (index === 0 && shouldPanToFirst) {
              // pan and zoom to the first new feature
              newFeaturePromises.push(parent.newFeature(feature).then((f) => {
                f.panAndZoom();
                return f;
              }));
            } else {
              newFeaturePromises.push(parent.newFeature(feature));
            }
          }
          // await the creation of the rest of the features
          const newFeatures = await Promise.all(newFeaturePromises);
          // if there is only one new feature, open the info window for it
          if (newFeatures.length === 1) {
            newFeatures[0].openInfoWindow({});
          }
          if (folders.length) {
            console.warn('folders not supported', folders);
          }
          finishedCallback();
        } catch (err) {
          console.error(err);
          this.addErrorMessage('There was an error loading the file.');
        }
        this.loading = false;
      };

      const handleFiles = async (event) => {
        /** @type {HTMLInputElement} */
        const target = event.target;
        const file = target.files[0];
        await loadFile(file);
        event.target.value = null;
      };

      this.$refs.fileUploadInput.addEventListener('change', handleFiles);
      this.$refs.fileUploadInput.click();
    },
  },
};
</script>

<style scoped lang="scss">
.is-loading {
  margin: auto;
  width: 50%;

  :deep(.loadIcon) {
    border-style: solid;
    border-color: white;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    border-top-color: transparent;
    animation: spin 2s infinite linear;
  }
}

.fileInput {
  visibility: hidden;
  position: absolute;
}
</style>
